import React, { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { CJEvent } from '@teetsh/app/src/types/event';
import { EventModal } from '../EventModal';
import { useUpdateCJSeance } from '../queries/useUpdateCJSeance';
import Dialog from '@teetsh/app/src/components/Dialog';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { useDeleteCJSeance } from '../queries/useDeleteCJSeance';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useAtom } from 'jotai';
import { editorCJSeancePayloadAtom } from './atom';
import { useCreateCJSeances } from '../queries/useCreateCJSeances';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import getClassroomsAvailableForThatDay from '@teetsh/app/src/components/EDT/getClassroomsAvailableForThatDay';
import getDay from '@teetsh/app/src/utils/time/getDay';
import { logNetworkError } from '@teetsh/app/src/utils/errors';
import { useTranslation } from 'react-i18next';
import { trackEvent } from 'queries/user/track';
import { useDraftCJSeance, useResetDraftCJSeance } from '../atoms';

export function CJSeanceUpdateModal() {
  const { school } = useSchoolSchoolyear();
  const { classroomsOrg } = school;
  const draftCJSeance = useDraftCJSeance();
  const resetDraftCJSeance = useResetDraftCJSeance();
  const [editorPayload, setPayload] = useAtom(editorCJSeancePayloadAtom);
  // Dans le cas où le users navigue sur un autre écran, il ne faut par lorsqu'il revient
  // que la popup soit ouverte
  useEffect(
    () => () => {
      setPayload(undefined);
    },
    [],
  );

  useEffect(() => {
    const hasDraftInEditMode = !!draftCJSeance?.id;
    if (hasDraftInEditMode && !editorPayload) {
      trackEvent('CahierJournal:resumeEditCJSeance');
      setPayload({
        seance: draftCJSeance,
      });
    }
  }, [draftCJSeance]);

  const updateEvent = useUpdateCJSeance();
  const removeSeance = useDeleteCJSeance();
  const createSeance = useCreateCJSeances();
  const { notifyError } = useNotification();
  const isMobile = useIsBelowBreakpoint('md');
  const onClose = () => {
    resetDraftCJSeance();
    setPayload(undefined);
  };
  const seance = editorPayload?.seance;
  const allClassrooms = useClassrooms();
  const startSeance = seance?.start;
  const { t } = useTranslation(['cahierJournal']);
  const classrooms = startSeance
    ? getClassroomsAvailableForThatDay(allClassrooms, getDay(startSeance))
    : allClassrooms;

  return (
    <Dialog
      full={isMobile}
      isOpen={!!editorPayload}
      size="2xl"
      position="top"
      onClose={onClose}
    >
      <EventModal
        allClassrooms={classrooms}
        closeModal={onClose}
        onDelete={() => {
          if (seance) {
            return removeSeance({
              seance,
              classroomId:
                school.classroomsOrg === 'unified'
                  ? undefined
                  : editorPayload?.classroomId,
            });
          }
          return Promise.resolve();
        }}
        onSubmit={values => {
          if (
            classroomsOrg === 'split' &&
            (seance?.classroomIds || []).length > 1 &&
            editorPayload?.classroomId
          ) {
            return Promise.all([
              createSeance({
                cjEvents: [
                  {
                    ...values,
                    id: uuid(),
                    classroomIds: [editorPayload?.classroomId],
                  },
                ],
              }).catch(err => {
                logNetworkError(
                  err,
                  "Couldn't create cj event when adding classroom",
                );
              }),
              removeSeance({
                seance: values,
                classroomId: editorPayload?.classroomId,
              }),
            ]).then(() => onClose());
          }
          if (!seance) {
            return Promise.resolve().then(() => onClose());
          }
          return updateEvent({
            event: values,
            previousDay: seance.start,
          }).catch(err => {
            logNetworkError(err, 'Failed to update cj seance', {
              event: values,
            });
            if (err?.response?.status === 403) {
              notifyError({
                title: t('cahierJournal:messages.unauthorized'),
              });
            } else {
              notifyError({
                title: t('cahierJournal:messages.updateError_one'),
              });
            }
          });
        }}
        event={seance as CJEvent}
      />
    </Dialog>
  );
}
